import type { PublicPlayerView, PublicRoomView, SecretRole } from "@secret-cabinet/contracts";
import { Check, Crown, Gavel, Skull, Vote, WifiOff } from "lucide-react";

export function PlayerRail({ room, compact = false }: { room: PublicRoomView; compact?: boolean }) {
  return (
    <section className={`player-rail${compact ? " player-rail--compact" : ""}`}>
      <header className="section-heading">
        <span>Attendance register</span>
        <b>
          {showVoteLedger(room) && room.lastGovernment
            ? `Votes ${room.lastGovernment.ballotsRevealed} / ${room.lastGovernment.ballotsTotal}`
            : `${room.players.filter((player) => player.alive).length} present`}
        </b>
      </header>
      <ol className="player-list">
        {room.players.map((player) => (
          <PlayerRow key={player.id} player={player} room={room} />
        ))}
      </ol>
    </section>
  );
}

function PlayerRow({ player, room }: { player: PublicPlayerView; room: PublicRoomView }) {
  const role = room.winner?.roles.find((entry) => entry.playerId === player.id)?.role;
  return (
    <li
      className={`player-row${!player.alive ? " player-row--dead" : ""}${
        !player.connected ? " player-row--offline" : ""
      }`}
    >
      <span className="player-seat">{String(player.seat).padStart(2, "0")}</span>
      <span className="player-name">
        <strong>{player.name}</strong>
        <small>{playerSubtitle(player, room, role)}</small>
      </span>
      <span className="player-state" aria-label={playerStateLabel(player, room)}>
        {!player.connected ? <WifiOff aria-hidden="true" /> : null}
        {!player.alive ? <Skull className="player-state__skull" aria-hidden="true" /> : null}
        {player.office === "president" ? <Crown aria-hidden="true" /> : null}
        {player.office === "chancellor" || player.office === "candidate" ? <Gavel aria-hidden="true" /> : null}
        {room.phase === "lobby" && player.ready ? <Check aria-hidden="true" /> : null}
        {player.ballotState === "cast" ? (
          <span className="vote-card vote-card--sealed" aria-label="Ballot locked, vote not yet revealed">LOCKED</span>
        ) : null}
        {player.ballotState === "revealed" && player.revealedBallot ? (
          <span className={`vote-card vote-card--${player.revealedBallot}`} aria-label={`Vote ${player.revealedBallot}`}>
            <Vote aria-hidden="true" />
            {player.revealedBallot.toUpperCase()}
          </span>
        ) : null}
      </span>
    </li>
  );
}

function playerSubtitle(
  player: PublicPlayerView,
  room: PublicRoomView,
  role: SecretRole | undefined,
): string {
  if (role) {
    return role === "hitler" ? "Hitler" : capitalise(role);
  }
  if (!player.alive) {
    return "Executed";
  }
  if (!player.connected) {
    return "Reconnecting";
  }
  if (player.office === "president") {
    return "President";
  }
  if (player.office === "chancellor") {
    return "Chancellor";
  }
  if (player.office === "candidate") {
    return "Chancellor candidate";
  }
  if (room.phase === "lobby") {
    return player.ready ? "Ready" : "Not ready";
  }
  if (room.phase === "role-reveal") {
    return player.roleAcknowledged ? "Dossier sealed" : "Reading dossier";
  }
  if (room.phase === "election") {
    return player.ballotState === "cast" ? "Ballot locked" : "Considering ballot";
  }
  if (showVoteLedger(room)) {
    if (player.ballotState === "revealed" && player.revealedBallot) {
      return `Vote ${player.revealedBallot.toUpperCase()}`;
    }
    if (player.ballotState === "cast") {
      return "Ballot locked · waiting to reveal";
    }
  }
  return "Member of the chamber";
}

function showVoteLedger(room: PublicRoomView): boolean {
  return Boolean(
    room.lastGovernment &&
      (room.lastGovernment.ballots.length > 0 ||
        (room.phase === "election-result" && room.phaseResult !== "veto-accepted") ||
        (room.phase === "game-over" && room.winner?.reason === "hitler-elected")),
  );
}

function playerStateLabel(player: PublicPlayerView, room: PublicRoomView): string {
  return playerSubtitle(player, room, undefined);
}

function capitalise(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}
